import { z } from 'zod';

export const HOME_SECTIONS = [
  'popular',
  'today',
  'collectionRecords',
  'continue',
  'watchLater',
  'collections',
  'random',
  'recommendations',
] as const;

export type HomeSection = (typeof HOME_SECTIONS)[number];

const DEFAULT_VISIBLE_HOME_SECTIONS: HomeSection[] = [
  'popular',
  'today',
  'collectionRecords',
  'collections',
  'random',
  'recommendations',
];
const DEFAULT_HIDDEN_HOME_SECTIONS: HomeSection[] = ['continue', 'watchLater'];
const defaultHomeLayout = () => ({
  visible: [...DEFAULT_VISIBLE_HOME_SECTIONS],
  hidden: [...DEFAULT_HIDDEN_HOME_SECTIONS],
});

const homeSectionSchema = z.enum(HOME_SECTIONS);
const homeLayoutSchema = z
  .object({
    visible: z.array(homeSectionSchema).default([...DEFAULT_VISIBLE_HOME_SECTIONS]),
    hidden: z.array(homeSectionSchema).default([...DEFAULT_HIDDEN_HOME_SECTIONS]),
  })
  .transform(({ hidden, visible }) => {
    const used = new Set<HomeSection>();
    const unique = (sections: HomeSection[]): HomeSection[] =>
      sections.filter((section) => {
        if (used.has(section)) {
          return false;
        }
        used.add(section);
        return true;
      });
    const normalizedVisible = unique(visible);
    const normalizedHidden = unique(hidden);
    for (const section of HOME_SECTIONS.filter((item) => !used.has(item))) {
      if (DEFAULT_HIDDEN_HOME_SECTIONS.includes(section)) {
        normalizedHidden.push(section);
      } else {
        normalizedVisible.push(section);
      }
    }
    return { visible: normalizedVisible, hidden: normalizedHidden };
  });

export const userConfigSchema = z
  .object({
    showNsfw: z.boolean().default(false),
    japaneseAnimeOnly: z.boolean().default(false),
    showLateNightAnime: z.boolean().default(false),
    scheduleFollowingOnly: z.boolean().default(false),
    useAiMediaSelection: z.boolean().default(false),
    aiSelectionPrompt: z.string().max(2_000).default(''),
    homeLayout: homeLayoutSchema.default(defaultHomeLayout()),
  })
  .strict();

export type UserConfig = z.infer<typeof userConfigSchema>;
export type HomeLayout = UserConfig['homeLayout'];
